import { useCallback } from "react";
import { queries } from "../../shared/queries";
import type { GrantRow } from "../../shared/rbac/crossResourceAvailability";
import type { GrantEntry } from "../../shared/rbac/roles";
import type { DisconnectImpact } from "../components/DisconnectWarning";
import { liveWorkflows } from "../lib/connector";
import {
  spacesLosingSupplierAccess,
  usersLosingConnectorAccess,
} from "../lib/spaceSupplierAccess";
import { useGroupMemberships } from "./useGroupMemberships";
import { useQuery } from "@rocicorp/zero/react";

// Client-side preview of what dropUsagesLostByGrantRemoval will disconnect for
// a proposed grant list. Best-effort: RBAC-filtered queries can under-list, so
// the server drop stays authoritative.
export function useConnectorDisconnectImpact(appId: string | null) {
  const [usageRows, usageResult] = useQuery(
    appId ? queries.connectorUsageDetails({ connectedAppId: appId }) : undefined
  );
  const [gitRows, gitResult] = useQuery(
    appId
      ? queries.loadConnectedAppGitRemoteUsage({ connectedAppId: appId })
      : undefined
  );
  const [spaces, spacesResult] = useQuery(
    queries.spaces({ settingsView: true })
  );
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const {
    userGroupIds: userGroupMemberships,
    memberUserIds,
    loaded: membershipsLoaded,
  } = useGroupMemberships();

  const computeImpact = useCallback(
    (
      scopeGrants: readonly GrantRow[],
      nextGrants: readonly GrantEntry[]
    ): DisconnectImpact => {
      const affectedUserIds = usersLosingConnectorAccess(
        scopeGrants,
        nextGrants,
        userGroupMemberships,
        memberUserIds,
        customRoles ?? []
      );
      const sharedSpaces = (spaces ?? []).filter((s) => s.mode !== "personal");
      const losing = new Set(
        spacesLosingSupplierAccess(
          "space_uses_connected_apps",
          sharedSpaces,
          scopeGrants,
          nextGrants,
          userGroupMemberships,
          customRoles ?? []
        ).map((s) => s.id)
      );
      if (losing.size === 0)
        return { workflows: [], gitSpaceNames: [], affectedUserIds };
      return {
        workflows: liveWorkflows(usageRows ?? []).filter((w) =>
          losing.has(w.spaceId)
        ),
        gitSpaceNames: (gitRows ?? []).flatMap((r) =>
          losing.has(r.refId) && r.space?.name ? [r.space.name] : []
        ),
        affectedUserIds,
      };
    },
    [
      spaces,
      customRoles,
      userGroupMemberships,
      memberUserIds,
      usageRows,
      gitRows,
    ]
  );
  const loaded =
    !appId ||
    (membershipsLoaded &&
      [usageResult, gitResult, spacesResult, rolesResult].every(
        (result) => result.type === "complete"
      ));
  return [computeImpact, loaded] as const;
}

// Best-effort count of personal chats the change would disconnect. Returns
// bare usage rows filtered by owner, so it counts chats the caller can't read
// without exposing them.
export function useAffectedChatCount(
  appId: string | null,
  userIds: readonly string[]
) {
  const [rows, result] = useQuery(
    appId && userIds.length > 0
      ? queries.connectorPersonalChatUsageForUsers({
          connectedAppId: appId,
          userIds: [...userIds],
          includeClosed: true,
        })
      : undefined
  );
  return [
    rows?.length ?? 0,
    !appId || userIds.length === 0 || result.type === "complete",
  ] as const;
}
